import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Box, TextField, Button, IconButton, Popover, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { STATUS } from '../../utils/status';
import { TableCellHead } from '../table/tableCell';
import { useStyles } from '../../assets/styles/listTableStyle';
import Error from '../error/error';
import TableRowItem from './taskCompletedTableRow';
import { useParams } from 'react-router-dom';
import { getCompletedAnalytics } from '../../redux/slices/analytics/completed';
import Pagination from './analyticsCompletedPagination';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { AnalyticsGranularity, getDateRangeForGranularity } from '../../utils/helper';

const AnalyticsListTable = ({ tableSwitch, title }: any) => {
  const classes = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const analytics = useSelector((state:RootState)=> state.analyticsCompleted.analyticsCompleted)
  const getStatus = useSelector((state: RootState) => state.analyticsCompleted.getStatus);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [sortColumn, setSortColumn] = useState<string>('date');
  const [sortOrder, setSortOrder] = useState<string>('asc');
  const dayDateRange = React.useMemo(() => getDateRangeForGranularity('day'), []);
  const [startDate, setStartDate] = useState(dayDateRange.startDate);
  const [endDate, setEndDate] = useState(dayDateRange.endDate);
  const [appliedStartDate, setAppliedStartDate] = useState(dayDateRange.startDate);
  const [appliedEndDate, setAppliedEndDate] = useState(dayDateRange.endDate);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const {id} = useParams()
  const association_type = useSelector((state: RootState) => state.projects.association_type);
  const [granularity, setGranularity] = useState<AnalyticsGranularity>('day');

  useEffect(()=>{
    if (!appliedStartDate || !appliedEndDate) {
      return;
    }
    dispatch(getCompletedAnalytics({project_id: id,page: currentPage, pageSize: itemsPerPage, sort_by: sortColumn, sort_order: sortOrder, association_type: association_type, granularity, start_date: appliedStartDate || undefined, end_date: appliedEndDate || undefined}))
   },[dispatch, id, currentPage, itemsPerPage, sortColumn, sortOrder, association_type, appliedStartDate, appliedEndDate, granularity])

   const totalPages = Math.ceil((analytics.count || 0) / itemsPerPage);

   const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const headers = ["DATE","ALLOCATED", "COMPLETED","PENDING","COMPLETION %","AHT"];

  const handleSort = (column: string) => {
    const formattedHeader = column.toLowerCase().replace(/\s+/g, '_');
    const newOrder = sortColumn === formattedHeader && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortColumn(formattedHeader);
    setSortOrder(newOrder);
  };

  const handleApplyDateFilter = () => {
    setCurrentPage(1);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setFilterAnchorEl(null);
  };

  const handleClearDateFilter = () => {
    setCurrentPage(1);
    const dateRange = getDateRangeForGranularity(granularity);
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
    setAppliedStartDate(dateRange.startDate);
    setAppliedEndDate(dateRange.endDate);
    setFilterAnchorEl(null);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const isFilterOpen = Boolean(filterAnchorEl);

  return (
      <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ marginLeft: '10px', fontFamily: 'Montserrat', fontWeight: 700, fontSize: '20px', lineHeight: '14px', visibility: title ? 'visible' : 'hidden' }}>
          {title || 'Tasks by Date'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={granularity}
            onChange={(_event, value: AnalyticsGranularity | null) => {
              if (!value) return;
              const dateRange = getDateRangeForGranularity(value);
              setGranularity(value);
              setStartDate(dateRange.startDate);
              setEndDate(dateRange.endDate);
              setAppliedStartDate(dateRange.startDate);
              setAppliedEndDate(dateRange.endDate);
              setCurrentPage(1);
            }}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: '12px',
                padding: '4px 10px',
              },
            }}
          >
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Filter by date range" placement="bottom-end" arrow>
            <IconButton
              aria-label="filter options"
              onClick={handleFilterClick}
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                color: 'white',
                backgroundColor: '#3D85C6',
                '&:hover': { backgroundColor: '#2F6EA8' }
              }}
            >
              <FilterAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Popover
        open={isFilterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 280, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>Date Filters</Typography>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" size="small" onClick={handleClearDateFilter} sx={{ textTransform: 'none' }}>
              Clear
            </Button>
            <Button variant="contained" size="small" onClick={handleApplyDateFilter} sx={{ textTransform: 'none' }}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>

      <TableContainer>
        {getStatus === STATUS.PENDING && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <CircularProgress />
          </div>
        )}
        {getStatus === STATUS.FAILED && (
          <Box sx={{ mt: 2 }}>
            <Error message={'Unable to load task-by-date analytics. Please adjust filters and try again.'} />
          </Box>
        )}
        {getStatus === STATUS.SUCESS && (
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead sx={{ backgroundColor: '#F7F9FA', '&:first-child td, &:first-child th': { borderBottom: 0 } }}>
            <TableRow>
                {headers.map((header) => (
                   <TableCell align="left" onClick={() => handleSort(header)} sx={{ fontSize: '10px', fontWeight: 700, lineHeight: '14px'}} key={header}>
                     {header}  <SwapVertIcon fontSize='small' sx={{marginLeft:'3px', cursor:'pointer'}}/>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              
            {getStatus === STATUS.SUCESS && analytics.results && analytics.results.length > 0 ? (
            <>
              {analytics.results.map((row: any) => (
                <TableRowItem
                  key={row.id}
                  row={row}
                />
              ))}
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={8} className={classes.noUsersMessage}>
                <Error message={'There is no data added as of yet.'} />
              </TableCell>
            </TableRow>
          )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          paginate={paginate}
          itemsPerPage={itemsPerPage} 
        />
    </>
  );
};

export default AnalyticsListTable;
